import React, {useRef, useState} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import { ChamberMemberCodebookModel } from 'app/shared/model/shared.model';
import { Loader } from 'app/shared/loader/loader';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT, MAX_FILE_SIZE, messages } from 'app/config/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { openFileInNewTab } from 'app/shared/util/file-utils';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import error = toast.error;
import { caseRevisionInitial, CaseRevisionModel } from 'app/edozvola/cases/case-revision/model/case-revision.model';
import { caseRevisionValidationSchema } from 'app/edozvola/cases/case-revision/model/case-revision.schema';
import {
  fetchCaseRevisionQuery,
  updateCaseRevisionQuery
} from 'app/edozvola/cases/case-revision/service/case-revision.queries';
import { getCaseRevisionFile } from './service/case-revision.service';
import { fetchChamberMembersQuery, fetchRevisionTypeOptionsQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { openFileInNewWindow } from 'app/shared/util/navigation-util';
import {faCircleNotch} from "@fortawesome/free-solid-svg-icons";

export interface IMainProjectRevisionFormProps {
  readOnly: boolean;
  setIdMainProjectRevision: any;
  idMainProjectRevision: string | null;
}

const CaseRevisionForm = (props: IMainProjectRevisionFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();

  const hiddenFileInput = useRef(null);

  const { data: chamberMemberOptions, isLoading: chamberMemberOptionsLoading } = fetchChamberMembersQuery();
  const { data: revisionTypeOptions, isLoading: revisionTypeOptionsLoading } = fetchRevisionTypeOptionsQuery();

  const { data: mainProjectRevisionData, isLoading: mainProjectRevisionDataLoading } = fetchCaseRevisionQuery(
    props.idMainProjectRevision,
  );

  const [previewAttachmentPending, setPreviewAttachmentPending] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: mainProjectRevisionData || caseRevisionInitial,
    validationSchema: caseRevisionValidationSchema,
    onSubmit: (values: CaseRevisionModel) => saveRevisionMutation(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveRevisionMutation, isPending: saveRevisionPending } = updateCaseRevisionQuery(uuid, formik.resetForm, () =>
    closeModal(),
  );

  const onChamberMemberChanged = (selected: ChamberMemberCodebookModel[]) => {
    formik.setFieldValue('authorizedAuditor.oib', selected[0] ? selected[0].oib : '');
    formik.setFieldValue('authorizedAuditor.authorizationNumber', selected);
  };

  const closeModal = () => {
    props.setIdMainProjectRevision(null);
  };

  const selectFile = () => {
    hiddenFileInput.current.click();
  };

  const extractFileNameWithoutExtension = (fileName: string, maxLength = 200): string => {
    if (!fileName) return '';

    const dotIndex = fileName.lastIndexOf('.');
    const nameWithoutExtension = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;

    return nameWithoutExtension.length > maxLength ? nameWithoutExtension.substring(0, maxLength) : nameWithoutExtension;
  };

  const extensionAllowed = (fileName: string): boolean => {
    return fileName.toLowerCase().endsWith('.pdf');
  };

  const appendFile = e => {
    const selectedFile = e.target.files[0];
    if (selectedFile.size > MAX_FILE_SIZE) {
      showToast(toast.error, messages.FILE_TOO_LARGE);
    } else if (!extensionAllowed(selectedFile.name)) {
      showToast(toast.error, messages.FILE_NOT_PDF);
    } else {
      formik.setFieldValue('fileWrapper.changed', true);
      formik.setFieldValue('revisionName', selectedFile ? extractFileNameWithoutExtension(selectedFile.name) : '');
      formik.setFieldValue('multipartFile', selectedFile).then(() => formik.validateForm());
    }
  };

  const downloadFile = () => {
    if (formik.values.fileWrapper) {
      setPreviewAttachmentPending(true);
      getCaseRevisionFile(formik.values.idCaseRevision)
        .then(data => {
          if (data) {
            openFileInNewWindow(data.url);
          }
        })
        .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'))
        .finally(() => setPreviewAttachmentPending(false));
    }
  };

  return (
    <>
      {!mainProjectRevisionDataLoading && (
        <Modal scrollable isOpen={props.setIdMainProjectRevision !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled dodanih revizija glavnog projekta</ModalHeader>
          ) : (
            <ModalHeader>
              {props.idMainProjectRevision === '' ? 'Unos revizije glavnog projekta' : 'Uređivanje revizije glavnog projekta'}
            </ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              {(mainProjectRevisionDataLoading || saveRevisionPending) && <Loader />}
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <div className={'form-header'}>Izvješće o kontroli glavnog projekta</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta revizije'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu revizije'}
                      onItemSelect={selected => formik.setFieldValue('revisionType', selected)}
                      selected={formik.values.revisionType}
                      labelKey={'value'}
                      options={revisionTypeOptionsLoading ? [] : revisionTypeOptions}
                      disabled={props.readOnly}
                      error={(formik.errors.revisionType as string) ?? ''}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Izvješće'} />
                    {props.readOnly ? (
                      <Input
                        disabled={props.readOnly}
                        onClick={selectFile}
                        value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName}
                        error={!formik.values.multipartFile && !formik.values.fileWrapper ? messages.MANDATORY_FIELD : ''}
                      />
                    ) : (
                      <Input
                        disabled={props.readOnly}
                        onClick={selectFile}
                        value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName}
                        error={!formik.values.multipartFile && !formik.values.fileWrapper ? messages.MANDATORY_FIELD : ''}
                        addonAppend={
                        previewAttachmentPending ?
                          <FontAwesomeIcon
                            icon={faCircleNotch}
                            spin={true}
                          /> :
                          <FontAwesomeIcon
                            icon={formik.values.fileWrapper ? 'download' : 'paperclip'}
                            onClick={formik.values.fileWrapper ? downloadFile : selectFile}
                          />
                        }
                      />
                    )}
                  </Col>
                  <input type="file" ref={hiddenFileInput} onChange={appendFile} className="hidden-upload" />
                  <Col md={6}>
                    <Label labelText={'Naziv revizije'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.revisionName}
                      error={formik.errors.revisionName ? `${formik.errors.revisionName}` : ''}
                      maxLength={200}
                      onChange={event => formik.setFieldValue('revisionName', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Dodatni opis'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.additionalDescription}
                      error={formik.errors.additionalDescription ? `${formik.errors.additionalDescription}` : ''}
                      maxLength={200}
                      onChange={event => formik.setFieldValue('additionalDescription', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Oznaka revizije'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.revisionMark}
                      error={formik.errors.revisionMark ? `${formik.errors.revisionMark}` : ''}
                      maxLength={50}
                      onChange={event => formik.setFieldValue('revisionMark', event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Datum izrade'} />
                    <Field
                      id="revisionDate"
                      name="revisionDate"
                      render={({ field }) => (
                        <FormikDatepicker
                          field={field}
                          disabled={props.readOnly}
                          setFieldValue={formik.setFieldValue}
                          dateFormat={APP_DATE_FORMAT}
                          error={formik.errors.revisionDate ? `${formik.errors.revisionDate}` : ''}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Ovlašteni revident</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Broj ovlaštenja/Ime i prezime'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi broj ovlaštenja/ime i prezime'}
                      onItemSelect={onChamberMemberChanged}
                      selected={formik.values.authorizedAuditor.authorizationNumber}
                      disabled={props.readOnly}
                      labelKey={'value'}
                      options={chamberMemberOptionsLoading ? [] : chamberMemberOptions}
                      error={
                        formik.errors.authorizedAuditor && formik.errors.authorizedAuditor['authorizationNumber']
                          ? (formik.errors.authorizedAuditor['authorizationNumber'] as string)
                          : ''
                      }
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'OIB'} />
                    <Input disabled value={formik.values.authorizedAuditor.oib} />
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={saveRevisionPending}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default CaseRevisionForm;
